import { makeRequest } from "../../../../services/apiClient";
import { getOCELMetadata } from "../../../../services/api/ocel.api";

// Coalesced metadata fetch when a fileType is known (shares one in-flight
// request app-wide); the no-fileType fallback resolves to the server-global
// active file and is kept only for legacy callers.
const fetchMetadata = (fileType, label) =>
  fileType ? getOCELMetadata(fileType) : makeRequest('/ocel/metadata', {}, label);

/**
 * Fetches the complete list of available object types from the backend.
 * Updated to work with dynamic files and OCEL metadata.
 *
 * @param {string} fileType - Optional file type to get object types for (defaults to active file)
 * @returns {Promise<string[]>} A list of object type names.
 */
export const getObjectTypes = async (fileType = null) => {
  try {
    console.log('🔄 Fetching object types for file type:', fileType || 'active file');

    // Try to get from OCEL metadata first (new way)
    try {
      const metadata = await fetchMetadata(fileType, 'OCEL metadata for object types');

      if (metadata && metadata.object_types) {
        const objectTypes = Object.keys(metadata.object_types);
        console.log('✅ Object types from metadata:', objectTypes);
        return objectTypes;
      }
    } catch (metadataError) {
      console.warn('⚠️ Could not get object types from metadata, falling back to legacy endpoint');
    }

    // Fallback to legacy endpoint
    const response = await makeRequest(
      '/object-types',
      fileType ? { params: { file_type: fileType } } : {},
      'object types (legacy)'
    );

    if (response && (Array.isArray(response.objectTypes) || Array.isArray(response))) {
      const objectTypes = Array.isArray(response.objectTypes) ? response.objectTypes : response;
      console.log('✅ Object types from legacy endpoint:', objectTypes);
      return objectTypes;
    }

    throw new Error('Invalid object types response format');
  } catch (error) {
    console.error('❌ Error fetching object types:', error);
    throw new Error(`Failed to fetch object types: ${error.message}`);
  }
};

/**
 * Fetches available activity types for the current or specified file.
 * New function that works with the OCEL Manager system.
 *
 * @param {string} fileType - Optional file type to get activities for (defaults to active file)
 * @returns {Promise<string[]>} A list of activity names.
 */
export const getActivityTypes = async (fileType = null) => {
  try {
    console.log('🔄 Fetching activity types for file type:', fileType || 'active file');

    // Try to get from OCEL metadata first
    try {
      const metadata = await fetchMetadata(fileType, 'OCEL metadata for activity types');

      if (metadata && metadata.activity_types) {
        const activityTypes = Object.keys(metadata.activity_types);
        console.log('✅ Activity types from metadata:', activityTypes);
        return activityTypes;
      }
    } catch (metadataError) {
      console.warn('⚠️ Could not get activity types from metadata, falling back to legacy endpoint');
    }

    // Fallback to legacy endpoint
    const response = await makeRequest(
      '/activity-types',
      fileType ? { params: { file_type: fileType } } : {},
      'activity types (legacy)'
    );

    if (response && (Array.isArray(response.activityTypes) || Array.isArray(response))) {
      const activityTypes = Array.isArray(response.activityTypes) ? response.activityTypes : response;
      console.log('✅ Activity types from legacy endpoint:', activityTypes);
      return activityTypes;
    }

    throw new Error('Invalid activity types response format');
  } catch (error) {
    console.error('❌ Error fetching activity types:', error);
    throw new Error(`Failed to fetch activity types: ${error.message}`);
  }
};

/**
 * Fetches available attributes for filtering from the current or specified file.
 * New function that works with the OCEL Manager system.
 *
 * @param {string} fileType - Optional file type to get attributes for (defaults to active file)
 * @returns {Promise<string[]>} A list of available attribute names.
 */
export const getAvailableAttributes = async (fileType = null) => {
  try {
    console.log('🔄 Fetching available attributes for file type:', fileType || 'active file');

    const metadata = await fetchMetadata(fileType, 'OCEL metadata for attributes');

    if (metadata && metadata.available_attributes) {
      console.log('✅ Available attributes from metadata:', metadata.available_attributes);
      return metadata.available_attributes;
    }

    // Return default attributes if none found
    const defaultAttributes = ['M88', 'M_SET', 'M_DOKU'];
    console.log('⚠️ No attributes found in metadata, returning defaults:', defaultAttributes);
    return defaultAttributes;
  } catch (error) {
    console.error('❌ Error fetching available attributes:', error);
    throw new Error(`Failed to fetch available attributes: ${error.message}`);
  }
};

/**
 * Gets comprehensive metadata for a file including object types, activities, and attributes.
 * New function that provides all metadata in one call.
 *
 * @param {string} fileType - Optional file type to get metadata for (defaults to active file)
 * @returns {Promise<object>} Complete metadata object
 */
export const getFileMetadata = async (fileType = null) => {
  try {
    console.log('🔄 Fetching complete file metadata for:', fileType || 'active file');

    const metadata = await fetchMetadata(fileType, 'complete file metadata');

    console.log('✅ Complete metadata received:', metadata);
    return metadata;
  } catch (error) {
    console.error('❌ Error fetching file metadata:', error);
    throw new Error(`Failed to fetch file metadata: ${error.message}`);
  }
};

/**
 * Utility function to handle histogram API errors with user-friendly messages.
 *
 * @param {Error} error - The error to handle
 * @returns {object} Formatted error information
 */
export const handleHistogramError = (error) => {
  console.error('🚨 Histogram API error:', error);

  const errorInfo = {
    message: 'An error occurred while fetching histogram data',
    details: error.message,
    isRetryable: false,
    suggestedAction: 'Please try again later'
  };

  // Handle specific error types
  if (error.message.includes('File not found')) {
    errorInfo.message = 'Dataset not found';
    errorInfo.suggestedAction = 'Please check if the file exists or upload a new dataset';
  } else if (error.message.includes('Invalid object type')) {
    errorInfo.message = 'Invalid object type selected';
    errorInfo.suggestedAction = 'Please select a valid object type for this dataset';
  } else if (error.message.includes('timeout')) {
    errorInfo.message = 'Request timed out';
    errorInfo.isRetryable = true;
    errorInfo.suggestedAction = 'The request took too long. Please try again.';
  } else if (error.message.includes('network')) {
    errorInfo.message = 'Network error';
    errorInfo.isRetryable = true;
    errorInfo.suggestedAction = 'Please check your internet connection and try again.';
  } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
    errorInfo.message = 'Server error';
    errorInfo.isRetryable = true;
    errorInfo.suggestedAction = 'There was a server error. Please try again in a few moments.';
  }

  return errorInfo;
};
